import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SidenavColor } from '../../crm-theme';

/// Selos da lista de quadros.
export const DEFAULT_BADGE_TINT = '#12b76a';
export const INTAKE_BADGE_TINT = '#3457e0';
const CREATE_COLOR = '#9cbcff';
const REOPEN_COLOR = '#7ddba4';

/// O menu tem as cores do menu lateral, não as do quadro: é o mesmo lugar de navegação.
export interface PickerColors {
    surface: string;
    text: string;
    textSoft: string;
    border: string;
    hoverBg: string;
    accent: string;
    white: string;
}

interface SidenavState {
    sidenavColor: SidenavColor;
    darkMode: boolean;
    whiteSidenav: boolean;
}

export const pickerColors = (theme: Theme, { sidenavColor, darkMode, whiteSidenav }: SidenavState): PickerColors => {
    const { palette } = theme;
    // Cinza no cinza sumiria: com o menu lateral escuro ou claro, o destaque usa o azul.
    const accentKey: SidenavColor = (darkMode ? ['dark'] : ['light']).includes(sidenavColor) ? 'info' : sidenavColor;
    const text = whiteSidenav ? palette.dark.main : palette.white.main;

    let surface: string;
    if (whiteSidenav) surface = palette.white.main;
    else if (darkMode) surface = palette.background.sidenav;
    else surface = theme.functions.linearGradient(palette.gradients.dark.main, palette.gradients.dark.state);

    return {
        surface,
        text,
        textSoft: alpha(text, 0.65),
        border: alpha(text, 0.12),
        hoverBg: alpha(text, 0.08),
        accent: palette[accentKey]?.main ?? palette.info.main,
        white: palette.white.main,
    };
};

export const pickerPaperSx = (pc: PickerColors): SxProps<Theme> => ({
    background: pc.surface,
    border: `1px solid ${pc.border}`,
    borderRadius: '13px',
    boxShadow: '0 26px 70px rgba(0,0,0,.45)',
    minWidth: 400,
});

export const searchFieldSx = (pc: PickerColors): SxProps<Theme> => ({
    '& .MuiOutlinedInput-root': {
        bgcolor: pc.hoverBg,
        borderRadius: '8px',
        '& fieldset': { borderColor: pc.border },
        '&:hover fieldset': { borderColor: alpha(pc.text, 0.28) },
    },
    '& input': { color: pc.text, fontSize: 13 },
    '& input::placeholder': { color: pc.textSoft, opacity: 1 },
});

export const boardItemSx = (pc: PickerColors): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 1,
    mx: 1.5,
    my: 0.5,
    borderRadius: '8px',
    color: pc.text,
    '&:hover': { bgcolor: alpha(pc.accent, 0.28) },
    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: pc.accent, color: pc.white },
});

/// No item selecionado o fundo já é o destaque: o selo vira branco translúcido.
export const badgeSx = (pc: PickerColors, tint: string, selected: boolean, textColor: string): SxProps<Theme> => ({
    height: 17,
    fontSize: 9.5,
    fontWeight: 700,
    flexShrink: 0,
    bgcolor: selected ? alpha(pc.white, 0.22) : alpha(tint, 0.16),
    color: textColor,
    '& .MuiChip-label': { px: 0.75 },
});

const footerButtonSx = (pc: PickerColors, color: string, hoverColor: string): SxProps<Theme> => ({
    flex: 1,
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color,
    '&:hover': { color: hoverColor, bgcolor: pc.hoverBg },
});

export const createButtonSx = (pc: PickerColors): SxProps<Theme> => footerButtonSx(pc, CREATE_COLOR, CREATE_COLOR);

/// Fechar é discreto; reabrir chama atenção, porque o quadro fechado não aceita mudanças.
export const toggleActiveButtonSx = (pc: PickerColors, active: boolean): SxProps<Theme> => (active
    ? footerButtonSx(pc, pc.textSoft, pc.text)
    : footerButtonSx(pc, REOPEN_COLOR, REOPEN_COLOR));
